import {
  type BefundZustand,
  type FunctionRole,
  type LifecycleStatus,
  SEGMENT_SCOPED_ROLES,
  type Segment,
} from './schemas/index.js';

/**
 * Geteilte Berechtigungsmatrix (V1.5 Rechte-Refactor).
 *
 * Single Source of Truth für Projekt-bezogene Aktionen — wird sowohl im Backend
 * (`apps/api/src/middleware/scope.ts` → Enforcement) als auch im Frontend
 * (Button-/Action-Gating) verwendet, damit beide nicht auseinanderdriften.
 *
 * Achsen:
 * - Sichtbarkeit (`read`): alle Funktionsrollen lesen global. Eingrenzung passiert
 *   nur über abschaltbare Persona-Default-Filter im Frontend.
 * - Fähigkeit (alle übrigen Actions): persona-/segment-gebunden, hier definiert.
 */
/**
 * Alle Projektaktionen als Liste (PV-037). Der Typ `ProjectAction` wird daraus abgeleitet, damit
 * die Rechte-Matrix der Hilfe (`hilfe/rechte-matrix.ts`) und der Bezeichnungs-Test in
 * `apps/web/src/locales/parity.test.ts` über ALLE Aktionen laufen — eine neue Aktion ohne
 * Bezeichnung lässt den Test scheitern (A5).
 */
export const PROJECT_ACTIONS = [
  'read',
  'change_phase',
  'edit',
  'edit_intake',
  'manage_tags',
  'staffing_manage',
  'staffing_resolve',
  'kfml_worklist_manage',
  'briefing_manage',
  'assign_lead_manager',
  'sync',
  // PV-052: Befund-Aktionen. Entscheidung braucht den Befund-Bezug (4. Parameter).
  'befund_zurueckstellen',
  'befund_nachhaken',
  // PV-052 L2: PL-Antwort beim Schließ-Check
  'befund_antworten',
  // PV-052 L2: „final geschlossen" von Hand setzen/entfernen
  'projekt_final_setzen',
  // #158: Besetzung ausplanen (KFML/Admin und die PL des Projekts)
  'besetzung_ausplanen',
] as const;
export type ProjectAction = (typeof PROJECT_ACTIONS)[number];

export type FunctionRoleAssignment = {
  role: FunctionRole;
  scope_segment: Segment | null;
};

/** Minimaler User-Shape, den Backend (`AuthenticatedUser`) und Frontend (`SessionUser`) erfüllen. */
export type PermissionUser = {
  user_id: string;
  function_roles: FunctionRoleAssignment[];
  /** Einzige Entscheidungs-Achse für Admin-Rechte (aus `users.is_admin`). */
  is_admin: boolean;
};

/**
 * Scope-relevante Projektfelder. segment + responsibility liegen am führenden Auftrag (V1.5).
 *
 * Neues Feld hier → `rechteFaelle`/`fallProjekt` in `hilfe/rechte-matrix.ts` prüfen (PV-037):
 * sonst zeigt die Rechtetabelle der Hilfe eine neue Bedingung in keinem Fall.
 */
export type ProjectScopeShape = {
  project_id: string;
  segment: Segment;
  project_lead_user_id: string | null;
  manager_user_id: string | null;
  mtb_user_id: string | null;
  responsibility: 'PU' | 'Service' | 'MTB' | null;
  /**
   * Steht das Projekt in der Eingangsphase? Traegt den Zuteilungsschutz aus #78.
   *
   * Quelle ist `phases.is_intake`, NICHT der Phasenname: Ein namensbasierter Riegel wurde hier
   * schon einmal gebaut und ist durch Umbenennen still aushebelbar.
   *
   * Bewusst **Pflichtfeld**. Optional waere es die gefaehrlichere Wahl — ein Aufrufer, der es
   * vergisst, bekaeme `undefined`, das als „nicht in der Eingangsphase" gelesen wird und den
   * Schutz lautlos aufhebt. So bricht stattdessen die Typpruefung.
   */
  phase_is_intake: boolean;
  /**
   * Lebenszyklus (#123). `merged` = zusammengeführt → nur lesen, für jede Rolle.
   * Bewusst Pflichtfeld (gleiche Begründung wie `phase_is_intake`): vergessen bricht die
   * Typprüfung statt den Schreibschutz still aufzuheben.
   */
  lifecycle_status: LifecycleStatus;
};

export function scopeSegmentsForRole(user: PermissionUser, role: FunctionRole): Segment[] {
  const out: Segment[] = [];
  for (const r of user.function_roles) {
    if (r.role === role && r.scope_segment) out.push(r.scope_segment);
  }
  return out;
}

/**
 * Befund-Bezug für `befund_*`-Aktionen (PV-052, architektur.md §6). Alles wird zur Laufzeit
 * ermittelt, nie gespeichert: die aufgelösten zuständigen Personen, die Manager der Tags am
 * Projekt, und (L2, `befund_antworten`) der aktuelle Zustand des Befunds selbst — damit die
 * „kein zweites Mal antworten"-Regel EINMAL in `canDoProjectAction` entschieden wird statt
 * zusätzlich als eigene Bedingung in den Sichten (sicht.ts) dupliziert zu werden.
 */
export type BefundBezug = {
  zustaendig_user_ids: readonly string[];
  tag_manager_user_ids: readonly string[];
  zustand: BefundZustand;
};

/**
 * Aufsicht über die Befunde eines Projekts (anforderung.md „Aufsicht"):
 * - Admin, KFML (`kaufm_leitung`): alle Projekte
 * - BL: Projekte im Segment einer seiner `bereichsleiter`-Rollen
 * - Manager: `projects.manager_user_id` (SF-Fachbereichsleiter) ODER ein Tag mit
 *   `tags.manager_user_id` = ich. Bewusst OHNE Funktionsrolle und NICHT über das Segment —
 *   die Aufsicht folgt der Zuordnung.
 * - PL, MTB, Service-Leitung: keine Aufsicht.
 */
export function hatBefundAufsicht(
  user: PermissionUser,
  project: Pick<ProjectScopeShape, 'segment' | 'manager_user_id'>,
  tagManagerUserIds: readonly string[],
): boolean {
  if (user.is_admin) return true;
  if (user.function_roles.some((r) => r.role === 'kaufm_leitung')) return true;
  if (scopeSegmentsForRole(user, 'bereichsleiter').includes(project.segment)) return true;
  if (project.manager_user_id === user.user_id) return true;
  return tagManagerUserIds.includes(user.user_id);
}

/**
 * Zentrale Entscheidungsfunktion. Siehe Ziel-Matrix:
 * - admin                → alles
 * - kaufm_leitung (KL)   → global read/edit/change_phase/edit_intake/staffing_manage + **staffing_resolve**. Tags nicht.
 * - bereichsleiter       → read global; edit/change_phase/edit_intake/manage_tags/staffing_manage nur eigenes Segment. Kein resolve.
 * - manager              → read global; edit/change_phase/edit_intake/staffing_manage nur eigenes Segment. KEIN manage_tags, kein resolve. (Tag-Verantwortung = nur Default-Filter.)
 * - mtb (Bereichsleitung)→ read global; edit_intake + staffing_manage + briefing_manage nur
 *                          eigenes Segment (KEIN responsibility-Check). Kein edit/change_phase/resolve.
 * - service_leitung      → read global; edit/change_phase/edit_intake/staffing_manage nur wenn
 *                          responsibility=Service UND eigenes Segment. Kein resolve.
 * - projektleiter        → read global; edit/change_phase/edit_intake/staffing_manage nur eigene Projekte. Kein resolve.
 * - ohne Funktionsrolle  → nur read (Betrachter). Kein generisches Schreibrecht mehr.
 *
 * MB-A `briefing_manage` (Mission-Briefing + Anhänge verwalten): admin global; vor der
 * Schleife projektbezogene Verantwortliche (Owner/PL, manager_user_id, mtb_user_id);
 * kaufm_leitung global; bereichsleiter/manager eigenes Segment; mtb (FR) eigenes Segment;
 * service_leitung wenn responsibility=Service UND eigenes Segment; Fallback ohne FR: nur
 * Verantwortliche (s.o.), sonst false.
 *
 * `sync` (Einzelprojekt-Sync „Aus Salesforce neu laden" im Projektdetail, #49):
 * admin + kaufm_leitung global; bereichsleiter/manager/mtb/service_leitung eigenes Segment —
 * bei mtb UND service_leitung ABWEICHEND ohne responsibility-Check; projektleiter nur auf
 * eigenen Projekten (Owner/Manager); Betrachter ohne Funktionsrolle NIE.
 * Begründung für das Segment- statt Zuteilungs-Scoping: Der häufigste Anlass ist eine in
 * Salesforce **geänderte Zuteilung**, die geholt werden soll — der neu zugewiesene PL/Manager/MTB
 * steht in der Projektverwaltung ja noch nicht drin und käme über einen Zuteilungs-Check nie an
 * den Sync heran (Henne-Ei). Fachlich unkritisch, weil der Lauf nur den SF-Stand nachzieht
 * (Allowlist in sync/runner.ts schützt operative Felder) und der Refresh-Sync dasselbe ohnehin
 * zweimal täglich tut — der Button zieht es nur vor.
 *
 * `kfml_worklist_manage` (KFML-Worklist-Haken am Projekt setzen/entfernen, PV-016):
 * admin + kaufm_leitung global; bereichsleiter/manager/mtb/service_leitung nur eigenes
 * Segment — bei service_leitung ABWEICHEND ohne responsibility-Check; projektleiter und
 * Betrachter ohne Funktionsrolle NIE (auch nicht auf eigenen Projekten).
 * Nicht zu verwechseln mit der KFML-Todoliste (services/kfml-todo.ts, LLM, kein Recht).
 *
 * Hinweis zur MTB-Zweistufigkeit: Die Funktionsrolle `mtb` = MTB-Bereichsleitung
 * (globales Lesen + Intake im eigenen Segment). Projektbezogen zugewiesene MTBs werden über
 * `projects.mtb_user_id` identifiziert und erhalten **kein** implizites Sonderrecht
 * aus dieser Funktion — ihre Rechte kommen aus ihrer eigenen System-/Funktionsrolle.
 *
 * Segment-Pflicht (bereichsleiter/manager/mtb/service_leitung): ohne gesetztes `scope_segment`
 * (`null`, Altbestand) liefert `scopeSegmentsForRole` eine leere Liste → `includes()` ist immer
 * `false` → keine Änderungsrechte mehr, nur noch globales `read`.
 *
 * `befund_zurueckstellen` / `befund_nachhaken` (PV-052): brauchen den 4. Parameter
 * `befund` (sonst false). Zurückstellen: zuständige Person, Aufsicht, Admin. Nachhaken:
 * Aufsicht, Admin. Aufsicht siehe `hatBefundAufsicht`.
 *
 * `befund_antworten` (PV-052 L2): nur der PL des Projekts (`project_lead_user_id`), Admin.
 * Bewusst ohne Funktionsrolle und ohne Aufsicht — die Antwort ist eine Aussage des PL. Der
 * Service prüft zusätzlich, dass die Regel eine Antwort kennt.
 *
 * Fix-Runde 1 (#105 L2): Solange die letzte PL-Antwort noch bei der KFML liegt (Zustand
 * `angefragt`), darf NIEMAND — auch Admin nicht — ein zweites Mal antworten. Das ist eine
 * Datenregel (ein Zustand, kein Rechteproblem), deshalb VOR dem Admin-Kurzschluss geprüft;
 * sonst zeigte die Oberfläche einem Admin einen Knopf, den der Server dann mit 409
 * `befund_bereits_beantwortet` zurückweist. Ohne `befund`-Bezug bleibt das Verhalten
 * unverändert (Alt-Aufrufer, die den Bezug nicht mitgeben).
 *
 * `projekt_final_setzen` (PV-052 L2): „final geschlossen" von Hand setzen/entfernen — nur
 * `kaufm_leitung` und Admin.
 */
export function canDoProjectAction(
  user: PermissionUser,
  project: ProjectScopeShape,
  action: ProjectAction,
  befund?: BefundBezug | undefined,
): boolean {
  // #123: Ein zusammengeführtes Projekt ist nur noch Nachweis — auch Admins schreiben nicht.
  if (project.lifecycle_status === 'merged') return action === 'read';

  // Fix-Runde 1 (#105 L2): kein zweites Mal antworten, solange die Antwort noch bei der KFML
  // liegt — gilt für jede Rolle, auch Admin, deshalb vor dem Admin-Kurzschluss.
  if (action === 'befund_antworten' && befund?.zustand === 'angefragt') return false;

  if (user.is_admin) return true;

  // PV-052 L2: Die PL-Antwort gibt nur der PL des Projekts. Muss VOR der Rollen-Schleife stehen:
  // deren Zweige (KL global, BL im Segment) gäben sonst jedem Leitungsmitglied das Recht.
  if (action === 'befund_antworten') return project.project_lead_user_id === user.user_id;

  // PV-052 L2: „final" von Hand nur die KFML (A11). Vor der Schleife — deren BL-/Manager-Zweige
  // gäben sonst im eigenen Segment jede Aktion frei.
  if (action === 'projekt_final_setzen') {
    return user.function_roles.some((r) => r.role === 'kaufm_leitung');
  }

  // #158: Ausplanen darf KFML/Admin (`canResolveStaffing`) und die PL des Projekts. Vor der
  // Rollen-Schleife — deren BL-/Manager-Zweige im Segment gäben sonst auch diesen Rollen das Recht.
  if (action === 'besetzung_ausplanen') {
    return canResolveStaffing(user) || project.project_lead_user_id === user.user_id;
  }

  // PV-052: Befund-Aktionen folgen Zuständigkeit und Aufsicht, nicht der Rollen-Matrix
  // darunter (deren Segment-Zweige würden z. B. jedem Manager im Segment Aufsicht geben).
  // Ohne Befund-Bezug: nie (fail-closed).
  if (action === 'befund_zurueckstellen' || action === 'befund_nachhaken') {
    if (!befund) return false;
    const aufsicht = hatBefundAufsicht(user, project, befund.tag_manager_user_ids);
    if (action === 'befund_nachhaken') return aufsicht;
    return aufsicht || befund.zustaendig_user_ids.includes(user.user_id);
  }

  const isOwner = project.project_lead_user_id === user.user_id;
  const isManager = project.manager_user_id === user.user_id;
  const isService = project.responsibility === 'Service';

  // briefing_manage (MB-A): projektbezogene Verantwortliche dürfen das Mission-Briefing
  // + Anhänge verwalten — unabhängig von (und auch ganz ohne) Funktionsrolle: PL (Owner),
  // Manager am Projekt, sowie die projektbezogen zugewiesene MTB (`projects.mtb_user_id`).
  if (
    action === 'briefing_manage' &&
    (isOwner || isManager || project.mtb_user_id === user.user_id)
  )
    return true;

  // assign_lead_manager (#78): Zuteilungsschutz in der Eingangsphase. Dort ist die Zuteilung
  // fachlich noch nicht entschieden — sie gehoert der kaufmaennischen Leitung. Ausserhalb der
  // Eingangsphase gilt unveraendert das normale Bearbeitungsrecht.
  //
  // Voll-Admins sind oben schon durch. `kuse_admin` erreicht diesen Zweig nicht: seine Rolle
  // gibt auf Projektebene ohnehin nur `read`.
  //
  // Der MTB bleibt ausdruecklich bei `edit` — er ist von diesem Riegel nicht betroffen
  // (anforderung.md §2.6).
  if (action === 'assign_lead_manager') {
    if (!project.phase_is_intake) return canDoProjectAction(user, project, 'edit');
    return user.function_roles.some((r) => r.role === 'kaufm_leitung');
  }

  // Fallback: keine Funktionsrolle → Betrachter. Liest global, ändert nichts.
  // (Einachsig: kein generisches Schreibrecht mehr ohne Funktionsrolle/is_admin.)
  if (user.function_roles.length === 0) {
    return action === 'read';
  }

  const blSegments = scopeSegmentsForRole(user, 'bereichsleiter');
  const mgrSegments = scopeSegmentsForRole(user, 'manager');
  const mtbSegments = scopeSegmentsForRole(user, 'mtb');
  const svcSegments = scopeSegmentsForRole(user, 'service_leitung');

  // staffing_resolve ist projektunabhängig (nur KL/admin) — siehe canResolveStaffing.
  if (action === 'staffing_resolve') return canResolveStaffing(user);

  for (const fr of user.function_roles) {
    switch (fr.role) {
      case 'kaufm_leitung':
        // KL: alles außer Tags pflegen. (staffing_resolve schon oben behandelt.)
        if (action !== 'manage_tags') return true;
        break;
      case 'bereichsleiter':
        if (action === 'read') return true;
        // edit / change_phase / edit_intake / manage_tags / staffing_manage → eigenes Segment
        if (blSegments.includes(project.segment)) return true;
        break;
      case 'manager':
        // Themenbereich-Manager: liest global; editiert eigenes Segment (wie BL), aber
        // pflegt KEINE Tags. Tag-Verantwortung steuert nur den Default-Filter (Frontend).
        if (action === 'read') return true;
        if (action === 'manage_tags') break;
        if (mgrSegments.includes(project.segment)) return true;
        break;
      case 'mtb':
        // MTB-Bereichsleitung: liest global; Intake/Staffing/Briefing/KFML-Worklist im
        // EIGENEN Segment. Bewusst KEIN responsibility-Check: die MTB arbeitet im
        // Presales/Intake auch auf Projekten, die (noch) nicht als Bereich "MTB"
        // klassifiziert sind.
        if (action === 'read') return true;
        if (
          (action === 'edit_intake' ||
            action === 'staffing_manage' ||
            action === 'briefing_manage' ||
            action === 'kfml_worklist_manage' ||
            action === 'sync') &&
          mtbSegments.includes(project.segment)
        )
          return true;
        break;
      case 'service_leitung':
        if (action === 'read') return true; // global lesen
        if (action === 'manage_tags') break;
        // KFML-Worklist: eigenes Segment, aber OHNE responsibility-Check. Der Haken ist eine
        // Vormerkung für die kaufmännische Runde des Segments (die alle Bereiche umfasst),
        // keine Änderung am Projektinhalt. Gleiche Begründung wie bei den Segment-Notizen.
        if (action === 'kfml_worklist_manage') {
          if (svcSegments.includes(project.segment)) return true;
          break;
        }
        // Einzel-Sync: eigenes Segment, ebenfalls OHNE responsibility-Check. Der Lauf zieht nur
        // den Salesforce-Stand nach und ändert nichts am Projektinhalt — und gerade der Wechsel
        // eines Projekts in den Service-Bereich ist einer der Anlässe, ihn auszulösen.
        if (action === 'sync') {
          if (svcSegments.includes(project.segment)) return true;
          break;
        }
        // edit/change_phase/edit_intake/staffing_manage: Service-Bereich UND eigenes Segment
        if (isService && svcSegments.includes(project.segment)) return true;
        break;
      case 'projektleiter':
        if (action === 'read') return true; // global lesen
        if (action === 'manage_tags') break;
        // Die KFML-Worklist ist die Arbeitsliste einer Leitungsrunde — ein PL setzt sich
        // nicht selbst auf die Tagesordnung. Gilt auch für eigene Projekte.
        if (action === 'kfml_worklist_manage') break;
        if (isOwner || isManager) return true; // eigene Projekte
        break;
      case 'kuse_admin':
        // KuSe-Admin hat in dieser Matrix (Projekt-Aktionen) KEIN Schreibrecht — ihre
        // Zuständigkeit ist Rollenvergabe (canAssignFunctionRole) + Tags (canManageTagInScope)
        // im eigenen Segment, beides projektlos. Ohne diesen Case fiele ein User mit NUR
        // kuse_admin ans Funktionsende durch → false auch für 'read', was die Domänenregel
        // „Lesen ist immer global" bricht (Review-Finding Task 3).
        if (action === 'read') return true;
        break;
    }
  }
  return false;
}

/**
 * Darf der User Staffing-Bedarfe **erledigen** (zuweisen / als besetzt oder geplant
 * markieren)? Projektunabhängig — nur Admin + Kaufm. Leitung. Bereichsrollen dürfen
 * Bedarfe nur anlegen/löschen/wieder öffnen (staffing_manage), nicht erledigen.
 */
export function canResolveStaffing(user: PermissionUser): boolean {
  if (user.is_admin) return true;
  return user.function_roles.some((r) => r.role === 'kaufm_leitung');
}

/**
 * Darf der User ein Projekt in die **gated Zielphase** schieben (#61)? Welcher
 * Übergang das ist, steht in `config/agent.yaml` (`phase_gate`) — heute
 * „Neu" → „Setup". Zusätzlich zum normalen `change_phase`-Recht, nicht statt
 * dessen: wer den Übergang gar nicht darf, scheitert schon vorher.
 *
 * Gleiche Prüfung wie `canResolveStaffing`, aber **bewusst eine eigene
 * Funktion**: Die beiden bedeuten Verschiedenes (Bedarf erledigen vs.
 * Eingangsstufe verlassen). Würden sie sich eine Implementierung teilen, änderte
 * eine spätere Rollen-Anpassung am Staffing still auch den Phasen-Riegel.
 */
export function canEnterGatedPhase(user: PermissionUser): boolean {
  if (user.is_admin) return true;
  return user.function_roles.some((r) => r.role === 'kaufm_leitung');
}

/**
 * Rollen mit Segment-Notiz-Schreibrecht (Teilmenge von `SEGMENT_SCOPED_ROLES`).
 *
 * ACHTUNG (F2/`kuse_admin`): `SEGMENT_SCOPED_ROLES` bedeutet nur „segmentpflichtig",
 * nicht „darf Segment-Notizen schreiben". `kuse_admin` ist segmentpflichtig (verwaltet
 * Rollen + Tags im eigenen Segment), bekommt darüber aber KEIN Notiz-Schreibrecht —
 * deshalb hier bewusst eine eigene, engere Liste statt `SEGMENT_SCOPED_ROLES` direkt zu
 * verwenden. Wer künftig einer weiteren Rolle Notizrechte geben will, muss sie explizit
 * hier eintragen.
 */
const SEGMENT_NOTE_WRITE_ROLES = [
  'bereichsleiter',
  'manager',
  'mtb',
  'service_leitung',
] as const satisfies readonly FunctionRole[];

/**
 * Darf der User im gegebenen Segment Segment-Notizen schreiben (anlegen / fertig
 * markieren / löschen / auf die KFML-Worklist setzen)?
 *
 * Projektlos und segmentgebunden — bewusst NICHT über canDoProjectAction, das eine
 * ProjectScopeShape verlangt (siehe architektur.md). Reiht sich neben canResolveStaffing
 * (projektunabhängig) und canManageTagInScope (segmentbezogen) ein.
 *
 * - is_admin                                     → true
 * - kaufm_leitung                                → true (segmentübergreifend, kein scope_segment)
 * - bereichsleiter/manager/mtb/service_leitung   → nur wenn scope_segment == segment
 * - kuse_admin / projektleiter / ohne Funktionsrolle → false (kuse_admin verwaltet nur
 *   Rollen + Tags im Segment, siehe `SEGMENT_NOTE_WRITE_ROLES`)
 *
 * Segment-Pflicht: eine Zuweisung ohne scope_segment (null, Altbestand) matcht nie →
 * false. Dieselbe „ohne Segment ⇒ kein Schreibrecht"-Semantik wie im Rechtemodell.
 */
export function canWriteSegmentNotes(user: PermissionUser, segment: Segment): boolean {
  if (user.is_admin) return true;
  const writeRoles = SEGMENT_NOTE_WRITE_ROLES as readonly FunctionRole[];
  for (const fr of user.function_roles) {
    if (fr.role === 'kaufm_leitung') return true;
    if (writeRoles.includes(fr.role) && fr.scope_segment === segment) return true;
  }
  return false;
}

/**
 * Darf der User ein Tag mit gegebenem `scopeSegment` pflegen (F2, nach shared gezogen,
 * Single Source of Truth für Backend + Frontend — vorher `apps/api/src/middleware/scope.ts`)?
 *
 * - is_admin        → ja, immer, auch globale Tags (`scopeSegment === null`)
 * - kaufm_leitung   → nein (§4-Matrix, unverändert)
 * - bereichsleiter  → ja im eigenen Segment, nicht für globale Tags
 * - kuse_admin (F2) → ja im eigenen Segment, nicht für globale Tags — analog bereichsleiter
 * - sonst           → nein
 *
 * Globale Tags (`scopeSegment === null`) bleiben bewusst `is_admin`-exklusiv — die
 * Guard-Zeile dafür ist unverändert aus der bisherigen `scope.ts`-Fassung übernommen.
 */
export function canManageTagInScope(user: PermissionUser, scopeSegment: Segment | null): boolean {
  if (user.is_admin) return true;
  if (scopeSegment === null) return false;
  for (const fr of user.function_roles) {
    if (
      (fr.role === 'bereichsleiter' || fr.role === 'kuse_admin') &&
      fr.scope_segment === scopeSegment
    )
      return true;
  }
  return false;
}

/**
 * Darf `actor` einem User die Funktionsrolle `targetRole` mit Segment `targetScope`
 * **vergeben ODER entziehen** (Entscheidung 2026-08-19)? Eine einzige Funktion für beide
 * Richtungen — der DELETE-Pfad (Rollenentzug, Task 5) ruft dieselbe Funktion mit
 * `targetRole`/`targetScope` der zu löschenden Zeile.
 *
 * - `actor.is_admin` → immer true.
 * - Sonst true NUR wenn: `actor` hat die Funktionsrolle `kuse_admin` mit
 *   `scope_segment === targetScope` UND `targetRole` ist segmentgescopt
 *   (`SEGMENT_SCOPED_ROLES`) UND `targetRole !== 'kuse_admin'`.
 *
 * Damit gesperrt: `is_admin`-Vergabe (läuft separat, kein Funktionsrollen-Feld), globale
 * Rollen (`kaufm_leitung`, `projektleiter` — nicht in `SEGMENT_SCOPED_ROLES`), Fremd-Segmente,
 * und KuSe-Admin-Ernennung/-Entzug durch KuSe-Admins selbst.
 *
 * KEIN Check des Personen-Segments des Ziel-Users — bewusst so entschieden: `users` hat
 * kein eigenes Segment-Feld, ein Segmentbezug entsteht überhaupt erst über die
 * Funktionsrollen-Zuweisung, die hier gerade geprüft wird.
 */
export function canAssignFunctionRole(
  actor: PermissionUser,
  targetRole: FunctionRole,
  targetScope: Segment | null,
): boolean {
  if (actor.is_admin) return true;
  if (targetRole === 'kuse_admin') return false;
  if (!(SEGMENT_SCOPED_ROLES as readonly FunctionRole[]).includes(targetRole)) return false;
  return actor.function_roles.some(
    (fr) => fr.role === 'kuse_admin' && fr.scope_segment === targetScope,
  );
}
